import { createContext, useContext, useEffect, useState, type CSSProperties, type ReactNode } from 'react';

// @outlevo/components: Readiness Cards, Revenue Actions, evidence and approval state for a host app.
// Data comes through the host's own server route (the fetcher), which holds the Outlevo key. These components
// never receive, store or send an API key (F15 FR5). Approvals always link to the Outlevo portal (FR8).

export type Fetcher = (path: string, init?: RequestInit) => Promise<Response>;
const Ctx = createContext<{ fetcher: Fetcher; portalUrl: string } | null>(null);

export function OutlevoProvider({ fetcher, portalUrl = 'https://app.outlevo.com', children }: { fetcher: Fetcher; portalUrl?: string; children: ReactNode }) {
  return <Ctx.Provider value={{ fetcher, portalUrl }}>{children}</Ctx.Provider>;
}

function useOutlevo() {
  const c = useContext(Ctx);
  if (!c) throw new Error('Wrap Outlevo components in <OutlevoProvider fetcher={…}>');
  return c;
}

/** Fetches JSON through the host's proxy. `initial` lets a server render with data it already has. */
export function useOutlevoData<T>(path: string | null, initial?: T) {
  const { fetcher } = useOutlevo();
  const [state, set] = useState<{ data?: T; error?: string; loading: boolean }>({ data: initial, loading: initial === undefined && path !== null });
  useEffect(() => {
    if (!path || initial !== undefined) return;
    let live = true;
    fetcher(path).then(async (r) => {
      const body = await r.json().catch(() => null);
      if (!live) return;
      set(r.ok ? { data: body as T, loading: false } : { error: (body as { error?: { message?: string } })?.error?.message ?? `HTTP ${r.status}`, loading: false });
    }).catch((e: Error) => live && set({ error: e.message, loading: false }));
    return () => { live = false; };
  }, [path]);
  return state;
}

// Theming: the host sets these CSS custom properties; every value has a fallback.
const t = {
  box: { fontFamily: 'var(--ol-font, system-ui, sans-serif)', color: 'var(--ol-text, #111827)', background: 'var(--ol-surface, #ffffff)', borderRadius: 'var(--ol-radius, 8px)', border: '1px solid color-mix(in srgb, var(--ol-muted, #6b7280) 30%, transparent)', padding: 16 } as CSSProperties,
  muted: { color: 'var(--ol-muted, #6b7280)', fontSize: 13 } as CSSProperties,
  button: { background: 'var(--ol-accent, #111827)', color: '#fff', border: 0, borderRadius: 'var(--ol-radius, 6px)', padding: '6px 12px', cursor: 'pointer', marginRight: 8 } as CSSProperties,
  danger: { background: 'transparent', color: 'var(--ol-danger, #b91c1c)', border: '1px solid var(--ol-danger, #b91c1c)', borderRadius: 'var(--ol-radius, 6px)', padding: '6px 12px', cursor: 'pointer' } as CSSProperties,
};

const Loading = () => <div style={t.muted} role="status">Loading…</div>;
const Failed = ({ error }: { error: string }) => <div style={{ ...t.muted, color: 'var(--ol-danger, #b91c1c)' }} role="alert">{error}</div>;

export type Evidence = { observation_id: string; text: string; source_url: string; observed_at: string };

export function EvidencePanel({ evidence }: { evidence: Evidence[] }) {
  return (
    <ul style={{ margin: '8px 0', paddingLeft: 18 }} data-ol="evidence">
      {evidence.map((e) => (
        <li key={e.observation_id} style={{ marginBottom: 4 }}>
          {e.text} <a href={e.source_url} target="_blank" rel="noopener noreferrer nofollow" style={t.muted}>source · {e.observed_at.slice(0, 10)}</a>
        </li>
      ))}
    </ul>
  );
}

/** A buying signal ("why now"): a dated public event with its source page. */
export type WhyNow = { type: string; headline: string; source_url: string; published_at: string | null; observed_at: string };

export function WhyNowPanel({ signals }: { signals: WhyNow[] }) {
  if (!signals.length) return null;
  return (
    <div data-ol="why-now" style={{ margin: '8px 0' }}>
      <strong style={{ fontSize: 13 }}>Why now</strong>
      <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
        {signals.map((w) => (
          <li key={`${w.type}:${w.source_url}:${w.headline}`} style={{ marginBottom: 4 }}>
            {w.headline} <a href={w.source_url} target="_blank" rel="noopener noreferrer nofollow" style={t.muted}>source · {(w.published_at ?? w.observed_at).slice(0, 10)}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}

type Card = {
  id: string; status: string; due_at: string | null;
  card: { company: { name: string; domain: string }; scores: { fit: number; intent: number; data_quality: number }; evidence: Evidence[]; unknowns: string[];
    contact: { name: string; title: string; verification: string }; reason: string; next_action: string; card_url: string; why_now?: WhyNow[] };
};

export const REJECT_REASONS = ['wrong_company', 'wrong_role', 'poor_icp_fit', 'wrong_geography', 'existing_customer_or_opportunity', 'wrong_timing', 'incorrect_data', 'no_relevant_use_case', 'other'] as const;

/** A Readiness Card. Accept and reject go through the host's proxy, which must use a key bound to the signed-in rep. */
export function ReadinessCard({ handoffId, initial, onDecision }: { handoffId: string; initial?: Card; onDecision?: (d: { decision: 'accept' | 'reject'; reason?: string }) => void }) {
  const { fetcher } = useOutlevo();
  const { data, error, loading } = useOutlevoData<Card>(`/v1/handoffs/${encodeURIComponent(handoffId)}`, initial);
  const [status, setStatus] = useState<string | undefined>();
  const [reason, setReason] = useState<(typeof REJECT_REASONS)[number]>('wrong_timing');
  if (loading) return <Loading />;
  if (error || !data) return <Failed error={error ?? 'Not found'} />;
  const c = data.card;
  const decide = async (decision: 'accept' | 'reject') => {
    const r = await fetcher(`/v1/handoffs/${encodeURIComponent(handoffId)}/${decision}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(decision === 'reject' ? { reason } : {}) });
    if (r.ok) { setStatus(decision === 'accept' ? 'accepted' : 'rejected'); onDecision?.({ decision, ...(decision === 'reject' ? { reason } : {}) }); }
  };
  const current = status ?? data.status;
  return (
    <article style={t.box} data-ol="readiness-card">
      <header>
        <strong>{c.company.name}</strong> <span style={t.muted}>{c.company.domain}</span>
        <div style={t.muted}>Fit {c.scores.fit} · Intent {c.scores.intent} · Data {c.scores.data_quality}{data.due_at ? ` · Due ${data.due_at.slice(0, 10)}` : ''}</div>
      </header>
      <p style={{ margin: '12px 0 4px' }}>{c.reason}</p>
      <p style={{ margin: '0 0 8px' }}><strong>Next:</strong> {c.next_action}</p>
      <WhyNowPanel signals={c.why_now ?? []} />
      <EvidencePanel evidence={c.evidence} />
      {c.unknowns.length ? <div style={t.muted}>Unknowns: {c.unknowns.join('; ')}</div> : null}
      <div style={{ margin: '8px 0' }}>{c.contact.name}, {c.contact.title} <span style={t.muted}>({c.contact.verification})</span></div>
      {current === 'delivered' ? (
        <div>
          <button type="button" style={t.button} onClick={() => decide('accept')}>Accept</button>
          <select aria-label="Reject reason" value={reason} onChange={(e) => setReason((e.target as unknown as { value: string }).value as typeof reason)} style={{ marginRight: 8 }}>
            {REJECT_REASONS.filter((r) => r !== 'other').map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
          </select>
          <button type="button" style={t.danger} onClick={() => decide('reject')}>Reject</button>
        </div>
      ) : <div style={t.muted}>Status: {current}</div>}
    </article>
  );
}

type Action = { id: string; action_type: string; execution_status: string; approval_status: string; reason: string; created_at: string };

export function RevenueActionList({ status, initial }: { status?: string; initial?: { data: Action[] } }) {
  const { data, error, loading } = useOutlevoData<{ data: Action[] }>(`/v1/revenue-actions${status ? `?status=${encodeURIComponent(status)}` : ''}`, initial);
  if (loading) return <Loading />;
  if (error || !data) return <Failed error={error ?? 'Not found'} />;
  return (
    <table style={{ ...t.box, borderCollapse: 'collapse', width: '100%' }} data-ol="revenue-actions">
      <thead><tr style={t.muted}><th align="left">Action</th><th align="left">Status</th><th align="left">Approval</th><th align="left">Why</th></tr></thead>
      <tbody>
        {data.data.map((a) => (
          <tr key={a.id}><td>{a.action_type.replace(/_/g, ' ')}</td><td>{a.execution_status}</td><td><ApprovalStatus status={a.approval_status} /></td><td>{a.reason}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

/** Shows approval state only. Approving happens in the Outlevo portal, never in the host app. */
export function ApprovalStatus({ status, policyId }: { status: string; policyId?: string }) {
  const ctx = useContext(Ctx);
  const label = status === 'approved' ? 'Approved' : status === 'rejected' ? 'Rejected' : status === 'required' ? 'Approval needed' : 'Pending';
  const color = status === 'approved' ? 'var(--ol-accent, #047857)' : status === 'rejected' ? 'var(--ol-danger, #b91c1c)' : 'var(--ol-muted, #6b7280)';
  return (
    <span data-ol="approval-status" style={{ color }}>
      {label}
      {status === 'required' || status === 'pending' ? (
        <> · <a href={`${ctx?.portalUrl ?? 'https://app.outlevo.com'}/approvals${policyId ? `/${encodeURIComponent(policyId)}` : ''}`} target="_blank" rel="noopener noreferrer">Review in Outlevo</a></>
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// The assistant (chat-first, as app.outlevo.com): your users run outbound by talking to it inside your app. Messages go
// through your proxy to /v1/assistant (needs assistant:read and assistant:write on the key). Tables render here;
// confirm cards spend credits only when your user presses Confirm; secret forms and starting a campaign open Outlevo.

type ACard =
  | { type: 'table'; title: string; columns: Array<{ key: string; label: string }>; rows: Array<Record<string, string | number | null>>; done?: unknown }
  | { type: 'confirm'; title: string; detail: string; credits?: number; calls: Array<{ method: 'POST' | 'PUT'; path: string; body?: unknown }>; done_text: string; done?: { text: string } }
  | { type: 'form'; kind: string; title: string; done?: unknown }
  | { type: 'launch'; campaign_id: string; title: string; done?: unknown }
  | { type: 'chart'; title: string; series: Array<{ day: string; sent: number; replied: number }>; done?: unknown }
  | { type: 'companies'; title: string; columns: Array<{ key: string; label: string }>; rows: Array<Record<string, string | number | null>>; records: Array<Record<string, unknown>>; done?: { text: string } };
type AMsg = { id: string; role: 'user' | 'assistant' | 'staff' | 'event'; body: string; author?: string | null; mine?: boolean; widgets?: ACard[]; choices?: { options: string[] }; links?: Array<{ path: string; label: string }> };

const bubble = (mine: boolean): CSSProperties => ({ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '88%', padding: '8px 11px', borderRadius: 12, whiteSpace: 'pre-wrap',
  background: mine ? 'var(--ol-accent, #2f6b4f)' : 'color-mix(in srgb, var(--ol-muted, #6b7280) 10%, transparent)', color: mine ? '#fff' : 'inherit' });
const newKey = () => (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);

export function OutlevoAssistant({ campaignId, placeholder = 'Ask Outlevo anything', page }: { campaignId?: string; placeholder?: string; page?: string }) {
  const { fetcher, portalUrl } = useOutlevo();
  const q = campaignId ? `?campaign_id=${encodeURIComponent(campaignId)}` : '';
  const [msgs, setMsgs] = useState<AMsg[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = async () => {
    const r = await fetcher(`/v1/assistant/thread${q}`);
    const b = await r.json().catch(() => null);
    if (r.ok) setMsgs(((b as { messages?: AMsg[] })?.messages ?? []));
    else setError((b as { error?: { message?: string } })?.error?.message ?? `HTTP ${r.status}`);
  };
  useEffect(() => { void load(); }, [campaignId]);
  const post = (path: string, body: unknown) => fetcher(path, { method: 'POST', headers: { 'content-type': 'application/json', 'idempotency-key': newKey() }, body: JSON.stringify(body) });
  const send = async (text: string) => {
    const message = text.trim();
    if (!message || busy) return;
    setBusy(true); setError(null); setDraft('');
    setMsgs((m) => [...m, { id: `u${Date.now()}`, role: 'user', body: message, mine: true }]);
    try {
      const r = await post('/v1/assistant/messages', { message, campaign_id: campaignId, page });
      if (!r.ok) { const b = await r.json().catch(() => null); setError((b as { error?: { message?: string } })?.error?.message ?? `HTTP ${r.status}`); }
      await load();
    } finally { setBusy(false); }
  };
  const [unticked, setUnticked] = useState<Record<string, number[]>>({});
  const confirmCard = async (m: AMsg, i: number, c: Extract<ACard, { type: 'confirm' }>) => {
    setBusy(true); setError(null);
    try {
      for (const call of c.calls) {
        const r = await fetcher(call.path, { method: call.method, headers: { 'content-type': 'application/json', 'idempotency-key': newKey() }, body: JSON.stringify(call.body ?? {}) });
        if (!r.ok) { const b = await r.json().catch(() => null); throw new Error((b as { error?: { message?: string } })?.error?.message ?? `HTTP ${r.status}`); }
      }
      await post(`/v1/assistant/messages/${m.id}/cards/${i}/done`, { text: c.done_text });
      await load();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const last = [...msgs].reverse().find((m) => m.role !== 'event');
  const choices = !busy && last?.role === 'assistant' ? last.choices?.options ?? [] : [];
  const card = (m: AMsg, c: ACard, i: number) => {
    const done = (c.done as { text?: string } | undefined)?.text;
    if (c.type === 'table') return (
      <table key={i} style={{ ...t.box, padding: 8, borderCollapse: 'collapse', width: '100%', fontSize: 13 }} data-ol="assistant-table">
        <caption style={{ ...t.muted, textAlign: 'left' }}>{c.title}</caption>
        <thead><tr>{c.columns.map((k) => <th key={k.key} align="left">{k.label}</th>)}</tr></thead>
        <tbody>{c.rows.map((r, j) => <tr key={j}>{c.columns.map((k) => <td key={k.key}>{String(r[k.key] ?? '—')}</td>)}</tr>)}</tbody>
      </table>
    );
    // companies found on the web: the end user unticks any and adds the rest (free), through the same Confirm path
    if (c.type === 'companies') {
      const off = unticked[`${m.id}:${i}`] ?? [];
      const keep = c.records.filter((_, j) => !off.includes(j));
      return (
        <div key={i} style={{ ...t.box, padding: 10 }} data-ol="assistant-companies">
          <b>{c.title}</b>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}><tbody>{c.rows.map((r, j) => (
            <tr key={j}><td><input type="checkbox" aria-label={`Keep ${String(r.name ?? r.domain)}`} checked={!off.includes(j)} disabled={Boolean(done) || busy}
              onChange={() => setUnticked({ ...unticked, [`${m.id}:${i}`]: off.includes(j) ? off.filter((x) => x !== j) : [...off, j] })} /></td>
              {c.columns.map((k) => <td key={k.key}>{String(r[k.key] ?? '—')}</td>)}</tr>
          ))}</tbody></table>
          {done ? <div style={{ color: 'var(--ol-accent, #047857)' }}>✓ {done}</div> : (
            <button type="button" style={{ ...t.button, marginTop: 8 }} disabled={busy || !keep.length}
              onClick={() => void confirmCard(m, i, { type: 'confirm', title: c.title, detail: '', calls: [{ method: 'POST', path: '/v1/companies/bulk', body: { records: keep } }], done_text: `Added ${keep.length} ${keep.length === 1 ? 'company' : 'companies'}` })}>
              Add {keep.length} {keep.length === 1 ? 'company' : 'companies'}
            </button>
          )}
        </div>
      );
    }
    if (c.type === 'confirm') return (
      <div key={i} style={{ ...t.box, padding: 10 }} data-ol="assistant-confirm">
        <b>{c.title}</b><div style={t.muted}>{c.detail}</div>
        {done ? <div style={{ color: 'var(--ol-accent, #047857)' }}>✓ {done}</div> : (
          <div style={{ marginTop: 8 }}>
            <button type="button" style={t.button} disabled={busy} onClick={() => void confirmCard(m, i, c)}>Confirm{c.credits ? ` · ${c.credits} credits` : ''}</button>
          </div>
        )}
      </div>
    );
    if (c.type === 'chart') {
      const max = Math.max(1, ...c.series.map((d) => d.sent));
      return <div key={i} style={{ ...t.box, padding: 10 }}><b>{c.title}</b><div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 60 }}>{c.series.map((d) => <span key={d.day} title={`${d.day}: ${d.sent} reached, ${d.replied} replied`} style={{ flex: 1, height: `${(d.sent / max) * 100}%`, minHeight: 2, background: 'var(--ol-accent, #2f6b4f)', opacity: 0.6 }} />)}</div></div>;
    }
    // secrets and starting a campaign stay in Outlevo
    const href = c.type === 'launch' ? `${portalUrl}/campaigns/${encodeURIComponent(c.campaign_id)}` : c.type === 'form' && c.kind === 'csv_import' ? `${portalUrl}/import` : `${portalUrl}/integrations`;
    return <div key={i} style={{ ...t.box, padding: 10 }}><b>{c.title}</b> · {done ? <span>✓ {done}</span> : <a href={href} target="_blank" rel="noopener noreferrer">Open in Outlevo</a>}</div>;
  };
  return (
    <div style={{ ...t.box, display: 'flex', flexDirection: 'column', gap: 10 }} data-ol="assistant">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 520, overflowY: 'auto' }} aria-live="polite">
        {msgs.map((m) => m.role === 'event'
          ? <div key={m.id} style={{ ...t.muted, alignSelf: 'center' }}>✓ {m.body}</div>
          : (
            <div key={m.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {m.role === 'user' && m.mine === false && m.author ? <span style={t.muted}>{m.author}</span> : null}
              <div style={bubble(m.role === 'user')}>{m.body}</div>
              {(m.widgets ?? []).map((c, i) => card(m, c, i))}
              {m.links?.length ? <div>{m.links.map((l) => <a key={l.path} href={`${portalUrl}${l.path}`} target="_blank" rel="noopener noreferrer" style={{ marginRight: 10 }}>{l.label}</a>)}</div> : null}
            </div>
          ))}
        {busy ? <div style={t.muted} role="status">Working…</div> : null}
      </div>
      {choices.length ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{choices.map((c) => <button key={c} type="button" style={{ ...t.danger, color: 'inherit', borderColor: 'currentColor' }} onClick={() => void send(c)}>{c}</button>)}</div> : null}
      {error ? <Failed error={error} /> : null}
      <form onSubmit={(e) => { e.preventDefault(); void send(draft); }} style={{ display: 'flex', gap: 8 }}>
        <input value={draft} onChange={(e) => setDraft((e.currentTarget as unknown as { value: string }).value)} placeholder={choices.length ? 'Something else? Type it here…' : placeholder} aria-label="Message to Outlevo"
          style={{ flex: 1, padding: '8px 10px', borderRadius: 'var(--ol-radius, 6px)', border: '1px solid color-mix(in srgb, var(--ol-muted, #6b7280) 40%, transparent)', font: 'inherit' }} />
        <button type="submit" style={t.button} disabled={busy || !draft.trim()}>Send</button>
      </form>
      <div style={{ ...t.muted, fontSize: 12 }}>Outlevo never approves, starts or sends on its own; credits are spent only when you confirm.</div>
    </div>
  );
}
